import { Directive, ElementRef, OnInit, OnDestroy, AfterViewInit, inject, input, output } from "@angular/core";
import { ScrollPosition } from "../utils/scroll-position";
import { ResizeObserverService } from "../utils/resize-observer";
import { Subject } from "rxjs";
import { takeUntil, debounceTime, filter, distinctUntilChanged } from "rxjs/operators";

export type ScrollBehavior = "smooth" | "instant" | "auto";

@Directive({
  standalone: true,
  selector: "[copilotStickToBottom]",
  providers: [ScrollPosition, ResizeObserverService],
})
export class StickToBottom implements OnInit, AfterViewInit, OnDestroy {
  enabled = input<boolean>(true);

  threshold = input<number>(10);

  initialBehavior = input<ScrollBehavior>("smooth");

  resizeBehavior = input<ScrollBehavior>("smooth");

  debounceMs = input<number>(100);

  isAtBottomChange = output<boolean>();

  scrollToBottomRequested = output<void>();

  private elementRef = inject(ElementRef);

  private scrollService = inject(ScrollPosition);

  private resizeService = inject(ResizeObserverService);

  private destroy$ = new Subject<void>();

  private contentElement?: HTMLElement;

  private wasAtBottom = true;

  private hasInitialized = false;

  private userHasScrolled = false;

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    const element = this.elementRef.nativeElement as HTMLElement;

    this.contentElement = element.querySelector("[data-stick-to-bottom-content]") as HTMLElement;
    if (!this.contentElement) {
      this.contentElement = element;
    }

    this.setupScrollMonitoring();
    this.setupResizeMonitoring();
    this.setupContentMutationObserver();

    setTimeout(() => {
      this.hasInitialized = true;
      if (this.enabled()) {
        this.scrollToBottom(this.initialBehavior());
      }
    }, 0);
  }

  private setupScrollMonitoring(): void {
    if (!this.enabled()) return;

    const element = this.elementRef.nativeElement;

    this.scrollService
      .monitorScrollPosition(element, this.threshold())
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(this.debounceMs()),
        distinctUntilChanged((a, b) => a.isAtBottom === b.isAtBottom),
      )
      .subscribe((state) => {
        const wasAtBottom = this.wasAtBottom;
        this.wasAtBottom = state.isAtBottom;

        if (!state.isAtBottom && wasAtBottom && this.hasInitialized) {
          this.userHasScrolled = true;
        } else if (state.isAtBottom) {
          this.userHasScrolled = false;
        }

        this.isAtBottomChange.emit(state.isAtBottom);
      });
  }

  private setupResizeMonitoring(): void {
    if (!this.enabled() || !this.contentElement) return;

    this.resizeService
      .observeElement(this.contentElement, 0, 250)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.enabled() && !this.userHasScrolled),
      )
      .subscribe((state) => {
        if (this.wasAtBottom && !state.isResizing) {
          this.scrollToBottom(this.resizeBehavior());
        }
      });

    const element = this.elementRef.nativeElement;
    this.resizeService
      .observeElement(element, 0, 250)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.enabled() && !this.userHasScrolled && this.wasAtBottom),
      )
      .subscribe(() => {
        this.scrollToBottom(this.resizeBehavior());
      });
  }

  private setupContentMutationObserver(): void {
    if (!this.enabled() || !this.contentElement) return;

    const mutationObserver = new MutationObserver(() => {
      if (this.enabled() && this.wasAtBottom && !this.userHasScrolled) {
        requestAnimationFrame(() => {
          this.scrollToBottom(this.resizeBehavior());
        });
      }
    });

    mutationObserver.observe(this.contentElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.destroy$.subscribe(() => {
      mutationObserver.disconnect();
    });
  }

  public scrollToBottom(behavior: ScrollBehavior = "smooth"): void {
    const element = this.elementRef.nativeElement;
    const smooth = behavior === "smooth";

    this.scrollService.scrollToBottom(element, smooth);
    this.userHasScrolled = false;
    this.scrollToBottomRequested.emit();
  }

  public isAtBottom(): boolean {
    return this.scrollService.isAtBottom(this.elementRef.nativeElement, this.threshold());
  }

  public getScrollState() {
    const element = this.elementRef.nativeElement;
    return {
      scrollTop: element.scrollTop,
      scrollHeight: element.scrollHeight,
      clientHeight: element.clientHeight,
      isAtBottom: this.isAtBottom(),
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
